import { buildLaravelMountUrl, getLaravelMountUrl, laravelPaths } from '@/lib/site';

export type AuthUserSnapshot = {
  role_id?: number | null;
  is_verified?: boolean | null;
  can_access_seller_dashboard?: boolean | null;
};

/** Admin + finance + manager + delivery staff roles. */
export function isStaffRole(roleId?: number | null): boolean {
  return roleId === 1 || roleId === 4 || roleId === 5 || roleId === 6;
}

export function shouldForceDashboard(user?: AuthUserSnapshot | null): boolean {
  if (!user) return false;
  if (isStaffRole(user.role_id)) return true;
  if (user.role_id === 2) return Boolean(user.can_access_seller_dashboard);
  return false;
}

export function dashboardUrlForRole(roleId?: number | null): string {
  if (isStaffRole(roleId)) return laravelPaths.adminDashboard();
  if (roleId === 2) return laravelPaths.sellerDashboard();
  return laravelPaths.dashboard();
}

function storefrontHomeUrl(): string {
  if (typeof window !== 'undefined' && window.location.origin) {
    return `${window.location.origin}/`;
  }
  const site = (process.env.NEXT_PUBLIC_SITE_URL ?? '').trim().replace(/\/$/, '');
  return site ? `${site}/` : '/';
}

function isSellerOnboardingPath(value: string): boolean {
  return /\/seller\/onboarding\//i.test(value);
}

/**
 * Resolve Laravel login redirect for the Next.js storefront modal.
 * Falls back to buyer account only when redirect is missing and role is unknown.
 */
export function resolvePostLoginRedirect(redirect: unknown, roleId?: number | null): string {
  const raw = typeof redirect === 'string' ? redirect.trim() : '';

  if (raw && isSellerOnboardingPath(raw)) {
    if (roleId != null) {
      return dashboardUrlForRole(roleId);
    }
    return storefrontHomeUrl();
  }

  if (raw) {
    if (/\/admin(\/|$)/i.test(raw)) {
      const match = raw.match(/\/admin[^\s?#]*/i);
      return buildLaravelMountUrl(match?.[0] ?? '/admin/dashboard');
    }
    if (/\/seller(\/|$)/i.test(raw)) {
      const match = raw.match(/\/seller[^\s?#]*/i);
      return buildLaravelMountUrl(match?.[0] ?? '/seller/dashboard');
    }
    if (/\/account(\/|$)/i.test(raw)) {
      const match = raw.match(/\/account[^\s?#]*/i);
      return buildLaravelMountUrl(match?.[0] ?? '/account');
    }

    try {
      const mountUrl = new URL(getLaravelMountUrl());
      const redirectUrl = new URL(raw, mountUrl.origin);
      const mountPath = mountUrl.pathname.replace(/\/$/, '');
      let appPath = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;

      if (mountPath && appPath.startsWith(`${mountPath}/`)) {
        appPath = appPath.slice(mountPath.length) || '/';
      } else if (appPath === mountPath) {
        appPath = '/';
      }

      if (
        appPath.startsWith('/admin') ||
        appPath.startsWith('/seller') ||
        appPath.startsWith('/account')
      ) {
        return buildLaravelMountUrl(appPath);
      }
    } catch {
      if (raw.startsWith('/admin') || raw.startsWith('/seller') || raw.startsWith('/account')) {
        return buildLaravelMountUrl(raw);
      }
    }
  }

  if (roleId != null) {
    return dashboardUrlForRole(roleId);
  }

  return laravelPaths.dashboard();
}

export async function resolvePostLoginRedirectWithUser(
  redirect: unknown,
  fetchUser: () => Promise<AuthUserSnapshot | null>,
  previousPageUrl?: string,
): Promise<string> {
  const raw = typeof redirect === 'string' ? redirect.trim() : '';
  const previous = previousPageUrl?.trim() || '';

  try {
    const user = await fetchUser();

    if (raw && isSellerOnboardingPath(raw)) {
      if (user?.role_id === 2 && user.can_access_seller_dashboard) {
        return laravelPaths.sellerDashboard();
      }
      if (user?.role_id != null) {
        return dashboardUrlForRole(user.role_id);
      }
      return previous || storefrontHomeUrl();
    }

    if (user && isStaffRole(user.role_id)) {
      return dashboardUrlForRole(user.role_id);
    }

    if (user?.role_id === 2) {
      if (user.can_access_seller_dashboard) {
        return laravelPaths.sellerDashboard();
      }
      return previous || storefrontHomeUrl();
    }

    if (raw && /\/seller\/dashboard/i.test(raw)) {
      return resolvePostLoginRedirect(redirect, user?.role_id);
    }

    if (raw && /\/admin(\/|$)/i.test(raw)) {
      return resolvePostLoginRedirect(redirect, user?.role_id);
    }

    if (previous) {
      return previousPageUrl!;
    }
  } catch {
    /* use fallbacks below */
  }

  if (previous && !raw) {
    return previousPageUrl!;
  }

  if (raw && /\/seller\/dashboard/i.test(raw)) {
    return resolvePostLoginRedirect(redirect);
  }

  if (raw && !/\/seller(\/|$)/i.test(raw)) {
    return resolvePostLoginRedirect(redirect);
  }

  return previous || storefrontHomeUrl();
}
